import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { getCurrentProvider } from "@/lib/provider";

const navItems = [
  { label: "لوحة التحكم", icon: "ri-dashboard-line", to: "/provider/dashboard" },
  { label: "الطلبات", icon: "ri-receipt-line", to: "/provider/orders" },
  { label: "الخدمات والأسعار", icon: "ri-price-tag-3-line", to: "/provider/services" },
  { label: "الفريق", icon: "ri-team-line", to: "/provider/team" },
  { label: "التقييمات", icon: "ri-star-line", to: "/provider/reviews" },
  { label: "التقارير المالية", icon: "ri-line-chart-line", to: "/provider/reports" },
  { label: "تقارير المبيعات", icon: "ri-bar-chart-box-line", to: "/provider/sales-reports" },
  { label: "التسويات", icon: "ri-bank-card-line", to: "/provider/settlements" },
  { label: "النطاق الجغرافي", icon: "ri-map-pin-line", to: "/provider/coverage" },
  { label: "الإشعارات", icon: "ri-notification-3-line", to: "/provider/notifications" },
  { label: "المستخدمون والصلاحيات", icon: "ri-user-settings-line", to: "/provider/users" },
  { label: "الإعدادات", icon: "ri-settings-3-line", to: "/provider/settings" },
  { label: "الدعم", icon: "ri-customer-service-2-line", to: "/provider/support" },
];

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [companyName, setCompanyName] = useState("مقدم خدمة");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let active = true;
    getCurrentProvider().then((p) => {
      if (active && p?.company_name) setCompanyName(p.company_name);
    });
    return () => {
      active = false;
    };
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSidebarOpen(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth/login", { replace: true });
  };

  const todayDate = new Date().toLocaleDateString("ar", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const initials = companyName.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join(" ");

  return (
    <div dir="rtl" className="flex h-screen w-full overflow-hidden bg-[#F0FDFD]">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar (right in RTL) */}
      <aside
        className={`fixed inset-y-0 right-0 z-50 flex h-full shrink-0 flex-col bg-gradient-to-b from-[#00858F] via-[#007A85] to-[#006B75] transition-all duration-300 md:static md:translate-x-0 ${
          collapsed ? "w-[76px] md:w-[76px]" : "w-[260px]"
        } ${sidebarOpen ? "translate-x-0" : "translate-x-full md:translate-x-0"}`}
      >
        {/* Logo + close + collapse */}
        <div
          className={`flex h-16 items-center gap-3 border-b border-white/10 ${
            collapsed ? "justify-center px-2" : "px-6"
          }`}
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white/10">
            <img
              src="https://storage.helloreaddy.io/project_files/9818337a-0a98-464f-85b4-cd986046343e/e4d8f675-e586-441e-9438-460bdea9c2ed_compressed_088ef075-be38-4fe0-a96e-7b50a08aee90.webp"
              alt="غسلة"
              className="h-7 w-7 object-contain"
            />
          </div>
          {!collapsed && (
            <span className="text-xl font-bold tracking-tight text-white">غسلة</span>
          )}
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className={`flex h-8 w-8 items-center justify-center rounded-lg text-white/70 hover:bg-white/10 md:hidden ${
              collapsed ? "" : "mr-auto"
            }`}
            aria-label="إغلاق"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Nav */}
        <nav className="mt-2 flex-1 space-y-1 overflow-y-auto px-3 py-2 dashboard-scroll">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 rounded-xl py-2.5 text-sm transition-all duration-200 ${
                  collapsed ? "justify-center px-0" : "px-3"
                } ${
                  isActive
                    ? "bg-white/10 text-white"
                    : "text-white/60 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute right-0 top-1/2 h-7 w-[3px] -translate-y-1/2 rounded-full bg-gradient-to-b from-[#00AAB7] via-[#00C4CF] to-[#33D1DA]" />
                  )}
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center transition-colors ${
                      isActive ? "text-[#33D1DA]" : ""
                    }`}
                  >
                    <i className={`${item.icon} text-lg`} />
                  </span>
                  {!collapsed && (
                    <span className="whitespace-nowrap">{item.label}</span>
                  )}
                  {collapsed && (
                    <span className="pointer-events-none absolute right-full z-50 mr-3 whitespace-nowrap rounded-lg bg-[#00565C] px-2.5 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100">
                      {item.label}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Collapse toggle */}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className={`mx-3 mb-2 hidden items-center gap-3 rounded-xl py-2.5 text-sm text-white/60 transition-all duration-200 hover:bg-white/5 hover:text-white md:flex ${
            collapsed ? "justify-center px-0" : "px-3"
          }`}
          aria-label={collapsed ? "توسيع القائمة" : "طي القائمة"}
          title={collapsed ? "توسيع القائمة" : "طي القائمة"}
        >
          <span className="flex h-5 w-5 shrink-0 items-center justify-center">
            <i
              className={`text-lg transition-transform duration-300 ${
                collapsed ? "ri-menu-unfold-line" : "ri-menu-fold-line"
              }`}
            />
          </span>
          {!collapsed && <span className="whitespace-nowrap">طي القائمة</span>}
        </button>

        {/* Provider footer */}
        <div className={`border-t border-white/10 p-4 ${collapsed ? "px-3" : ""}`}>
          <div
            className={`flex items-center gap-3 rounded-xl bg-white/5 p-2.5 transition-colors hover:bg-white/10 ${
              collapsed ? "flex-col justify-center" : ""
            }`}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#33D1DA] to-[#00AAB7] text-sm font-semibold text-white">
              {initials || "م"}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-white">{companyName}</p>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex cursor-pointer items-center gap-1 text-xs text-white/60 transition-colors hover:text-white"
                >
                  <i className="ri-logout-box-r-line" />
                  تسجيل خروج
                </button>
              </div>
            )}
            {collapsed && (
              <button
                type="button"
                onClick={handleLogout}
                className="flex h-8 w-8 items-center justify-center rounded-full text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="تسجيل خروج"
                title="تسجيل خروج"
              >
                <i className="ri-logout-box-r-line" />
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-[#E8F4F4] bg-white/80 px-4 backdrop-blur-md md:px-8">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-full text-ghasla-text-primary transition-colors hover:bg-[#F0FDFD] md:hidden"
              aria-label="القائمة"
            >
              <i className="ri-menu-line text-xl" />
            </button>
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              className="hidden h-10 w-10 items-center justify-center rounded-full text-ghasla-text-primary transition-colors hover:bg-[#F0FDFD] md:flex"
              aria-label={collapsed ? "توسيع القائمة" : "طي القائمة"}
            >
              <i
                className={`text-xl transition-transform duration-300 ${
                  collapsed ? "ri-menu-unfold-line" : "ri-menu-fold-line"
                }`}
              />
            </button>
            <div className="hidden h-8 w-8 items-center justify-center overflow-hidden rounded-lg bg-[#F0FDFD] md:flex">
              <img
                src="https://storage.helloreaddy.io/project_files/9818337a-0a98-464f-85b4-cd986046343e/e4d8f675-e586-441e-9438-460bdea9c2ed_compressed_088ef075-be38-4fe0-a96e-7b50a08aee90.webp"
                alt=""
                className="h-5 w-5 object-contain"
              />
            </div>
            <p className="text-base font-semibold text-ghasla-text-heading">مرحباً، {companyName}</p>
            <span className="hidden text-sm text-ghasla-text-secondary sm:inline">·</span>
            <span className="hidden text-sm text-ghasla-text-secondary sm:inline">مقدم خدمة</span>
          </div>
          <div className="flex items-center gap-3 md:gap-5">
            <span className="hidden text-sm text-ghasla-text-secondary md:inline">{todayDate}</span>
            <button
              type="button"
              onClick={() => navigate("/provider/notifications")}
              className="relative flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-ghasla-text-primary transition-colors hover:bg-[#F0FDFD]"
              aria-label="الإشعارات"
            >
              <i className="ri-notification-3-line text-xl" />
              <span className="absolute left-2 top-2 h-2 w-2 rounded-full bg-[#F59E0B]" />
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="dashboard-scroll flex-1 overflow-y-auto p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}